import React, { useState } from "react";
import styled from "styled-components";
import {
  Modal,
  Form,
  Row,
  Col,
  Select,
  InputNumber,
  DatePicker,
  Input,
  Button,
  Upload,
  message,
} from "antd";
import { WalletOutlined, InboxOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { uploadReceipt } from "../../lib/firebase/expenseModel";
import { getUnitDisplayAddress } from "../../lib/unitAddressUtils";

const { Item } = Form;
const { Option } = Select;
const { TextArea } = Input;

/** Schedule E categories - maps to IRS Schedule E lines 5-19 */
const SCHEDULE_E_CATEGORIES = [
  "Advertising",
  "Auto & Travel",
  "Cleaning & Maintenance",
  "Insurance",
  "Legal & Professional Fees",
  "Management Fees",
  "Mortgage Interest",
  "Repairs",
  "Supplies",
  "Taxes",
  "Utilities",
];

const ACCEPT_RECEIPT = "image/*,.pdf";
const MAX_RECEIPT_SIZE_MB = 10;

const AddExpenseModal = ({ open, onCancel, onSubmit, units = [], form }) => {
  const categoryValue = Form.useWatch("category", form);
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  const handleReceiptChange = (info) => {
    const file =
      info.fileList[0]?.originFileObj ?? info.file?.originFileObj ?? null;
    setReceiptFile(file || null);
  };

  const handleRemoveReceipt = () => setReceiptFile(null);

  const handleFinish = async (values) => {
    let receiptUrl = "";
    if (receiptFile) {
      const isLt10M = receiptFile.size / 1024 / 1024 < MAX_RECEIPT_SIZE_MB;
      if (!isLt10M) {
        message.error(`Receipt must be smaller than ${MAX_RECEIPT_SIZE_MB}MB.`);
        return;
      }
      setUploadingReceipt(true);
      try {
        receiptUrl = await uploadReceipt(receiptFile);
      } catch (err) {
        message.error(
          "Failed to upload receipt. You can save the expense without it."
        );
        setUploadingReceipt(false);
        return;
      }
      setUploadingReceipt(false);
    }
    onSubmit({ ...values, receiptUrl });
    setReceiptFile(null);
  };

  const handleCancel = () => {
    setReceiptFile(null);
    onCancel();
  };

  return (
    <StyledModal
      title={
        <ModalHeader>
          <WalletOutlined
            style={{
              color: "white",
              marginRight: "8px",
              filter: "brightness(0) invert(1)",
            }}
          />
          Add Expense
        </ModalHeader>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          date: dayjs(),
          amount: undefined,
          description: "",
        }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Item
              label="Date"
              name="date"
              rules={[{ required: true, message: "Please select a date" }]}
            >
              <DatePicker style={{ width: "100%" }} placeholder="Select date" />
            </Item>
          </Col>
          <Col xs={24} sm={12}>
            <Item
              label="Amount"
              name="amount"
              rules={[
                { required: true, message: "Enter amount" },
                {
                  validator: (_, value) => {
                    if (value === null || value === undefined || value === "")
                      return Promise.resolve();
                    const num = Number(value);
                    if (isNaN(num) || num <= 0)
                      return Promise.reject(
                        new Error("Amount must be greater than 0")
                      );
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="0.00"
                min={0.01}
                step={0.01}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                addonBefore="$"
              />
            </Item>
          </Col>
        </Row>

        <Item
          label="Payee / Vendor"
          name="payee"
          rules={[
            {
              validator: (_, value) => {
                if (
                  categoryValue === "Repairs" &&
                  (!value || String(value).trim() === "")
                ) {
                  return Promise.reject(
                    new Error(
                      "Vendor/Payee is required for Repairs. Please add who performed the work."
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder="e.g., Joe's Plumbing, Home Depot" />
        </Item>

        <Item
          label="Property / Unit"
          name="unitId"
          rules={[{ required: true, message: "Please select a unit" }]}
        >
          <Select
            placeholder="Select unit"
            showSearch
            optionFilterProp="children"
          >
            {units.map((unit) => (
              <Option key={unit.id} value={unit.id}>
                {unit.name || getUnitDisplayAddress(unit) || unit.id}
              </Option>
            ))}
          </Select>
        </Item>

        <Item label="Description (optional)" name="description">
          <TextArea rows={3} placeholder="Notes about this expense" />
        </Item>

        <Item
          label="Category"
          name="category"
          rules={[{ required: true, message: "Please select a category" }]}
        >
          <Select placeholder="Select Schedule E category">
            {SCHEDULE_E_CATEGORIES.map((cat) => (
              <Option key={cat} value={cat}>
                {cat}
              </Option>
            ))}
          </Select>
        </Item>

        <Item label="Receipt (optional)">
          <Upload.Dragger
            name="receipt"
            accept={ACCEPT_RECEIPT}
            maxCount={1}
            fileList={
              receiptFile
                ? [{ uid: "-1", name: receiptFile.name, status: "done" }]
                : []
            }
            beforeUpload={() => false}
            onChange={handleReceiptChange}
            onRemove={handleRemoveReceipt}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: "var(--color-primary)" }} />
            </p>
            <p className="ant-upload-text">
              Click or drag a receipt (image or PDF)
            </p>
            <p className="ant-upload-hint">
              Image or PDF, max 10MB · Optional for records and tax prep
            </p>
          </Upload.Dragger>
        </Item>

        <FormActions>
          <Button type="default" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={uploadingReceipt}>
            Log Expense
          </Button>
        </FormActions>
      </Form>
    </StyledModal>
  );
};

const FormActions = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;

  .ant-btn {
    flex: 1;
    height: 44px;
    border-radius: 12px;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    flex-direction: row;
    gap: 12px;
    margin-top: 20px;

    .ant-btn {
      height: 44px;
      font-size: 1rem;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  color: white;
  font-weight: 600;
  font-size: 18px;
`;

const StyledModal = styled(Modal)`
  .ant-modal-header {
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 16px 20px;
  }

  .ant-modal-title {
    color: white !important;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.2;
  }

  .ant-modal-close {
    color: white !important;
    font-size: 16px;
    top: 16px;
    right: 20px;

    .anticon,
    .anticon svg,
    svg {
      color: white !important;
      fill: white !important;
    }

    &:hover {
      color: rgba(255, 255, 255, 0.8) !important;
    }

    &:hover .anticon,
    &:hover .anticon svg,
    &:hover svg {
      color: rgba(255, 255, 255, 0.8) !important;
      fill: rgba(255, 255, 255, 0.8) !important;
    }
  }

  .ant-modal-body {
    padding: 24px;
    background: #fafafa;
    overflow-x: hidden;
    overflow-y: auto;
    max-height: calc(100vh - 120px);
  }

  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    max-width: 100vw;
    padding: 0px;
  }

  .ant-upload-wrapper .ant-upload.ant-upload-drag {
    height: 220px !important;
    min-height: 220px;
  }

  .ant-upload-drag-icon {
    margin-bottom: 8px !important;
  }

  .ant-upload-text {
    height: auto;
    min-height: 0;
    padding: 4px 10px;
    margin-bottom: 4px !important;
  }

  .ant-modal {
    max-width: 90vw;
    margin: 20px auto;
    top: 20px;
  }

  .ant-modal-wrap {
    overflow-y: auto;
  }

  /* Mobile optimizations */
  @media (max-width: 768px) {
    .ant-modal {
      max-width: 95vw;
      margin: 10px auto;
      top: 10px;
    }

    .ant-modal-body {
      padding: 16px;
      padding-bottom: 32px;
      max-height: calc(100vh - 100px);
    }

    .ant-modal-header {
      padding: 12px 16px;
    }

    .ant-modal-title {
      font-size: 16px;
    }

    .ant-modal-close {
      top: 12px;
      right: 16px;
      font-size: 14px;
    }

    .ant-upload-wrapper .ant-upload.ant-upload-drag {
      height: 200px !important;
      min-height: 200px;
    }
  }

  @media (max-width: 480px) {
    .ant-modal {
      max-width: 98vw;
      margin: 5px auto;
      top: 5px;
    }

    .ant-modal-body {
      padding: 12px;
      max-height: calc(100vh - 80px);
    }

    .ant-modal-header {
      padding: 10px 12px;
    }

    .ant-modal-title {
      font-size: 15px;
    }
  }
`;

export default AddExpenseModal;
